/**
 * The host's JavaScript as SwiftBrowser Native's engine (docs/native.md,
 * "The round trip"): for a module built with the round-trip target
 * (`Native/RoundTrip`, the native views compiled against the shim), this
 * answers its one import, `sb_native.request`, and exposes the native
 * functions Swift installs (`applyOps`, `log`, `now`, …) on `__sbNative`,
 * each a call of the module's `sb_native_invoke` export.
 *
 * The page (wasm.ts, for a module that imports `sb_native`) and Node
 * (scripts/run-wasi.mjs) use it the same way: merge `imports` into the
 * module's imports, instantiate, then `attach` the instance. Strings cross
 * linear memory as UTF-8; an answer to Swift is a buffer from `sb_alloc`
 * holding a little-endian 4-byte length and the bytes, which Swift frees,
 * and a result from Swift is the same layout, freed with `sb_native_free`
 * once read. `@swiftbrowser/native/wasm-host` re-exports it.
 */

export interface WasmNativeHostOptions {
  /** Runs a script at the global scope (default: an indirect `eval`). */
  evaluate?: (script: string, name: string) => void;
  /** The global object the bundle's globals live on (default `globalThis`). */
  global?: Record<string, unknown>;
  /**
   * Called after each installed function ran, with its name: the page runs
   * the module's executor after an `applyOps` that came from the page's own
   * loop (a fetch's answer, a timer), which no event follows.
   */
  afterInvoke?: (name: string) => void;
}

export interface WasmNativeHost {
  /** Merge into the module's import object before instantiating. */
  imports: {
    sb_native: {
      request: (kind: number, kindLength: number, a: number, aLength: number, b: number, bLength: number) => number;
    };
  };
  /** The instance, once instantiated: its memory and the exports the host calls. */
  attach(instance: NativeInstance): void;
  /** The `__sbNative` object as it stands (replaced by a reset). */
  readonly native: Record<string, (...args: unknown[]) => unknown>;
}

/** A `WebAssembly.Instance` (typed structurally: the Node build has no DOM lib). */
export interface NativeInstance {
  exports: object;
}

interface Exports {
  memory: { buffer: ArrayBuffer };
  sb_alloc: (length: number) => number;
  sb_native_invoke: (name: number, nameLength: number, args: number, argsLength: number) => number;
  sb_native_free: (pointer: number) => void;
}

/** The globals a bundle defines, dropped by a reset (`JSHost.reset`). */
const BUNDLE_GLOBALS = ['__sbDispatch', '__sbTimerFired', '__sbFetchResult', '__sbRefresh', '__sbVendor'];

export function wasmNativeHost(options: WasmNativeHostOptions = {}): WasmNativeHost {
  const g = options.global ?? (globalThis as unknown as Record<string, unknown>);
  const evaluate = options.evaluate ?? ((script: string) => (0, eval)(script));
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  let exports: Exports | null = null;
  let native: Record<string, (...args: unknown[]) => unknown> = {};

  const need = (): Exports => {
    if (!exports) throw new Error('wasmNativeHost: attach(instance) before the module runs');
    return exports;
  };
  const read = (pointer: number, length: number): string =>
    decoder.decode(new Uint8Array(need().memory.buffer, pointer, length));
  /** A buffer from `sb_alloc` with the text's length and bytes; Swift frees it. */
  const answer = (value: unknown): number => {
    const bytes = encoder.encode(JSON.stringify(value));
    const pointer = need().sb_alloc(bytes.length + 4);
    // The allocation may have grown the memory: take the view after it.
    const view = new DataView(need().memory.buffer);
    view.setUint32(pointer, bytes.length, true);
    new Uint8Array(need().memory.buffer, pointer + 4, bytes.length).set(bytes);
    return pointer;
  };
  const describe = (error: unknown): string => (error instanceof Error ? (error.stack ?? error.message) : String(error));

  /** A native function's call: into Swift and back with its JSON result. */
  const invoke = (name: string, args: unknown[]): unknown => {
    try {
      return call(name, args);
    } finally {
      options.afterInvoke?.(name);
    }
  };
  const call = (name: string, args: unknown[]): unknown => {
    const nameBytes = encoder.encode(name);
    const argsBytes = encoder.encode(JSON.stringify(args));
    const namePointer = need().sb_alloc(nameBytes.length || 1);
    new Uint8Array(need().memory.buffer, namePointer, nameBytes.length).set(nameBytes);
    const argsPointer = need().sb_alloc(argsBytes.length || 1);
    new Uint8Array(need().memory.buffer, argsPointer, argsBytes.length).set(argsBytes);
    const result = need().sb_native_invoke(namePointer, nameBytes.length, argsPointer, argsBytes.length);
    if (result === 0) return undefined;
    const length = new DataView(need().memory.buffer).getUint32(result, true);
    const text = read(result + 4, length);
    need().sb_native_free(result);
    const value: unknown = JSON.parse(text);
    if (value && typeof value === 'object' && 'error' in value && Object.keys(value).length === 1) {
      throw new Error(String((value as { error: unknown }).error));
    }
    return value;
  };

  const reset = (): void => {
    for (const name of BUNDLE_GLOBALS) delete g[name];
    native = {};
    g['__sbNative'] = native;
  };

  const request = (kind: number, kindLength: number, a: number, aLength: number, b: number, bLength: number): number => {
    const what = read(kind, kindLength);
    const first = read(a, aLength);
    const second = read(b, bLength);
    switch (what) {
      case 'reset':
        reset();
        return 0;
      case 'install':
        native[first] = (...args: unknown[]) => invoke(first, args);
        return 0;
      case 'eval':
        try {
          evaluate(first, second);
          return 0;
        } catch (error) {
          return answer({ error: describe(error) });
        }
      case 'call': {
        const fn = g[first];
        if (typeof fn !== 'function') return answer({ error: `${first} is not defined` });
        try {
          const result: unknown = (fn as (...args: unknown[]) => unknown)(...(JSON.parse(second) as unknown[]));
          return answer({ ok: result === undefined ? null : result });
        } catch (error) {
          return answer({ error: describe(error) });
        }
      }
      case 'has':
        return answer({ ok: first in g });
      default:
        return answer({ error: `unknown request ${what}` });
    }
  };

  reset();
  return {
    imports: { sb_native: { request } },
    attach(instance) {
      for (const name of ['memory', 'sb_alloc', 'sb_native_invoke', 'sb_native_free']) {
        if (!(name in instance.exports)) throw new Error(`wasmNativeHost: the module does not export ${name}`);
      }
      exports = instance.exports as unknown as Exports;
    },
    get native() {
      return native;
    },
  };
}
